import { ChangeEvent, useRef, useState } from "react";

import { Center, Flex, FlexProps } from "@chakra-ui/react";

import { Form } from "ui/Form/Form";
import { MinusIcon, PlusIcon } from "ui/Icons/Icons";
import { Input } from "ui/Input/Input";
import { TitleS } from "ui/Typography/Typography";
import { VerticalLayout } from "ui/VerticalLayout/VerticalLayout";

export type IncreaseDecreaseProps = {
  label?: string | React.ReactNode;
  count?: number;
  changeHandler: (count: number) => void;
  withInput?: boolean;
  isIncreaseBtnDisabled?: boolean;
  isDecreaseBtnDisabled?: boolean;
  allowNegativeInput?: boolean;
  dataTestId?: string;
} & FlexProps;

export function IncreaseDecrease({
  label,
  count = 0,
  changeHandler,
  withInput = false,
  isIncreaseBtnDisabled = false,
  isDecreaseBtnDisabled = false,
  allowNegativeInput = false,
  dataTestId,
  ...rest
}: IncreaseDecreaseProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value || "0";
    if (!Number.isNaN(parseInt(val, 10))) {
      changeHandler(parseInt(val, 10));
    }
  };

  const handleInputSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    inputRef.current?.blur();
  };

  const inputProps = {
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.currentTarget.select();
      setIsKeyboardVisible(true);
    },
    onBlur: () =>
      setTimeout(() => {
        setIsKeyboardVisible(false);
      }, 100),
    onSelect: (event: React.SyntheticEvent) => event.preventDefault(),
  };

  const isNegativeInputAllowed = allowNegativeInput || count;
  return (
    <VerticalLayout gap="s150" {...rest}>
      {label && <TitleS>{label}</TitleS>}
      <Flex justifyContent="space-between" alignItems="center">
        <Center
          backgroundColor="grey.100"
          borderRadius="sm"
          height="44px"
          minWidth="32px"
          maxWidth="100px"
          flex="1"
          cursor={isNegativeInputAllowed ? "pointer" : "auto"}
          data-testid={`${dataTestId}-decrement`}
          onClick={
            !isKeyboardVisible && isNegativeInputAllowed && !isDecreaseBtnDisabled
              ? () => changeHandler(count - 1)
              : undefined
          }
        >
          <MinusIcon
            boxSize="24px"
            color={isNegativeInputAllowed && !isDecreaseBtnDisabled ? "pinkFlink.500" : "grey.400"}
          />
        </Center>
        <Center px="s100">
          {withInput ? (
            <Form onSubmit={handleInputSubmit}>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={count}
                onChange={handleChange}
                fontWeight="bold"
                textAlign="center"
                variant="unstyled"
                borderRadius="sm"
                borderColor="grey.400"
                borderWidth="1px"
                width="42px"
                height="42px"
                data-testid={`${dataTestId}-input`}
                inputRef={inputRef}
                {...inputProps}
              />
            </Form>
          ) : (
            <strong data-testid={`${dataTestId}-count`}>{count}</strong>
          )}
        </Center>
        <Center
          backgroundColor="grey.100"
          borderRadius="sm"
          cursor="pointer"
          height="44px"
          minWidth="32px"
          maxWidth="100px"
          flex="1"
          data-testid={`${dataTestId}-increment`}
          onClick={() =>
            !isKeyboardVisible && !isIncreaseBtnDisabled ? changeHandler(count + 1) : undefined
          }
        >
          <PlusIcon boxSize="24px" color={isIncreaseBtnDisabled ? "grey.400" : "pinkFlink.500"} />
        </Center>
      </Flex>
    </VerticalLayout>
  );
}
